import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
} from '@mui/material';

interface ReportHeaderProps {
  companyName: string;
  reportTitle: string;
  subtitle?: string;
  asOnDate?: string;
  fromDate?: string;
  toDate?: string;
  showLogo?: boolean;
  /** Filer's GSTIN — shown on statutory GST/TDS reports. */
  gstin?: string;
  /** Registered office address, single line. */
  address?: string;
}

const ReportHeader: React.FC<ReportHeaderProps> = ({
  companyName,
  reportTitle,
  subtitle,
  asOnDate,
  fromDate,
  toDate,
  showLogo = false,
  gstin,
  address,
}) => {
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getDateDisplay = () => {
    if (asOnDate) {
      return `As on ${formatDate(asOnDate)}`;
    }
    if (fromDate && toDate) {
      return `From ${formatDate(fromDate)} to ${formatDate(toDate)}`;
    }
    return '';
  };

  return (
    <Paper
      sx={{
        p: 3,
        mb: 3,
        textAlign: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)',
        borderBottom: '3px solid',
        borderColor: 'primary.main',
      }}
      elevation={0}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        {showLogo && (
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: 24,
            }}
          >
            {companyName.charAt(0)}
          </Box>
        )}
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              color: 'text.primary',
              letterSpacing: 1,
            }}
          >
            {companyName}
          </Typography>
          {address && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              {address}
            </Typography>
          )}
          {gstin && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.25, fontWeight: 600, color: 'text.secondary', letterSpacing: 0.4 }}
            >
              GSTIN: <Box component="span" sx={{ fontFamily: 'monospace' }}>{gstin}</Box>
            </Typography>
          )}
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: 'primary.main',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {reportTitle}
      </Typography>

      {subtitle && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5 }}
        >
          {subtitle}
        </Typography>
      )}

      {getDateDisplay() && (
        <Typography
          variant="body1"
          sx={{
            mt: 1,
            fontWeight: 500,
            color: 'text.secondary',
          }}
        >
          {getDateDisplay()}
        </Typography>
      )}
    </Paper>
  );
};

export default ReportHeader;
